<!--
  RecordsInput.vue

  Reusable component for selecting single or multiple records by ID.
  Works like LinkedInput but operates directly with record IDs instead of link IDs.
  Used for filter conditions where the value is a record ID or array of record IDs.

  Props:
    - modelValue: Single record ID (number) or array of record IDs for isArray mode
    - recordType: The type of record to search for (e.g., "organization", "person")
    - isArray: Whether to allow multiple selections
    - isEditable: Whether to show controls (default: true)
    - size: Input size ('small', 'normal', 'large')
    - placeholder: Custom placeholder text
    - disableLocked: List locked records but make them unselectable

  Emits:
    - update:modelValue: Emits updated record ID or array of record IDs
-->
<template>
  <div class="input chip-input record-input p-0 px-1" :class="`is-${size}`">
    <!-- Multiple records display -->
    <div v-if="isArray && selectedRecords.length" class="tags">
      <span
        v-for="r in selectedRecords"
        :key="r.id"
        class="tag tag-sm"
        :style="{ '--tag-color': getRecordColor(r) }"
      >
        <record-link :to="r" @click.stop>
          <span class="font-semibold">{{ r.name }}</span>
        </record-link>
        <span class="icon is-small" :style="{ display: isEditable ? '' : 'inline-block' }">
          <font-awesome-icon :icon="getRecordIcon(r)" />
        </span>
        <button v-if="isEditable" class="delete is-small" @click="handleRemove(r)"></button>
      </span>
    </div>

    <!-- Single record display -->
    <div v-else-if="selectedRecord" class="tags">
      <span class="tag tag-link tag-sm" :style="{ '--tag-color': getRecordColor(selectedRecord) }">
        <record-link :to="selectedRecord" @click.stop>
          <span class="font-semibold">{{ selectedRecord.name }}</span>
        </record-link>
        <span class="icon is-small" :style="{ display: isEditable ? '' : 'inline-block' }">
          <font-awesome-icon :icon="getRecordIcon(selectedRecord)" />
        </span>
        <button v-if="isEditable" class="delete is-small" @click="handleRemove()"></button>
      </span>
    </div>

    <!-- Autocomplete for adding records -->
    <RecordAutocomplete
      v-if="isEditable && recordType && (!selectedRecord || isArray)"
      ref="autocomplete"
      v-model="autocompleteRecord"
      :size="size"
      :record-type="recordType"
      :placeholder="placeholder || `${isArray ? 'Add a' : 'Select a'} ${recordType || 'record'}...`"
      :initial-name="initialName"
      :exclude-ids="excludeIds"
      :disable-locked="disableLocked"
      @selected="handleSelect"
    >
      <template v-if="$slots.empty" #empty="{ query }">
        <slot name="empty" :query="query" />
      </template>
    </RecordAutocomplete>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import type { SearchedRecord } from "@auger/core"
import { fetchRecordsByIds } from "@auger/core/gql"
import type { RequiredRecordFieldsFragment } from "@auger/core/gql/types"
import { getRecordColor, getRecordIcon } from "@/common/records"
import RecordAutocomplete from "@/components/autocompletes/RecordAutocomplete.vue"
import { client } from "@/init/apollo"

type CachedRecord = RequiredRecordFieldsFragment

interface Props {
  modelValue?: number | number[] | null
  recordType?: string | null
  isArray?: boolean
  isEditable?: boolean
  size?: string
  placeholder?: string | null
  excludeIds?: number[]
  disableLocked?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  recordType: null,
  isArray: false,
  isEditable: true,
  size: "normal",
  placeholder: null,
  excludeIds: () => [],
  disableLocked: false,
})

const emit = defineEmits<{
  "update:modelValue": [value: number | number[] | null]
  selected: [record: CachedRecord]
}>()

const recordCache = ref<Record<number, CachedRecord>>({})
const autocompleteRecord = ref({})
const initialName = ref("")
const autocomplete = ref<InstanceType<typeof RecordAutocomplete> | null>(null)

const selectedIds = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return []
  return Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
})

const selectedRecord = computed(() => {
  if (props.isArray || !selectedIds.value.length) return null
  const firstId = selectedIds.value[0]
  if (firstId === undefined) return null
  return recordCache.value[firstId] || null
})

const selectedRecords = computed(() => {
  if (!props.isArray) return []
  return selectedIds.value
    .map((id) => recordCache.value[id])
    .filter((r): r is CachedRecord => Boolean(r))
})

watch(
  () => props.modelValue,
  () => {
    loadMissingRecords()
  },
  { immediate: true }
)

function loadMissingRecords() {
  const missingIds = selectedIds.value.filter((id) => !recordCache.value[id])
  if (!missingIds.length) return

  client
    .query({
      query: fetchRecordsByIds,
      variables: { ids: missingIds },
      fetchPolicy: "network-only",
    })
    .then(({ data }) => data?.records ?? [])
    .then((records) => {
      records.forEach((record) => {
        recordCache.value[record.id] = record
      })
    })
    .catch((error: unknown) => {
      console.error("Error fetching records:", error)
    })
}

function handleSelect(record: SearchedRecord | null) {
  if (!record) return

  const cached: CachedRecord = record
  recordCache.value[cached.id] = cached
  emit("selected", cached)

  if (props.isArray) {
    const currentIds = Array.isArray(props.modelValue) ? props.modelValue : []
    if (!currentIds.includes(cached.id)) {
      emit("update:modelValue", [...currentIds, cached.id])
    }
    initialName.value = ""
  } else {
    emit("update:modelValue", cached.id)
  }
  autocomplete.value?.clear()
}

function handleRemove(record?: CachedRecord) {
  if (props.isArray && record) {
    const currentIds = Array.isArray(props.modelValue) ? props.modelValue : []
    const updatedIds = currentIds.filter((id) => id !== record.id)
    emit("update:modelValue", updatedIds.length ? updatedIds : null)
  } else {
    emit("update:modelValue", null)
  }
}

defineExpose({ clear: () => autocomplete.value?.clear() })
</script>

<style>
.record-input {
  outline: none;

  input.autocomplete-input.input {
    border: none;
    outline: none;
    box-shadow: none;
    background: none;
    padding: 0;
    font-size: inherit;
    height: auto;
  }

  .tags {
    margin: 0;
    flex-shrink: 0;
  }

  .autocomplete {
    flex: 1;
    min-width: 120px;
  }

  .tag.tag-link {
    opacity: 0.75;
    padding-right: 4px;

    a {
      color: var(--auger-text-inverted);
    }

    .icon {
      margin-left: 8px;
      padding-right: 2px;
      margin-inline-end: 3px;
    }

    button.delete {
      display: none;
      margin-inline-start: 7px;
      margin-inline-end: 4px;
      padding-right: 5px;
    }

    &:hover {
      opacity: 1;
      transition: opacity 0.2s ease;

      .icon {
        display: none;
      }

      button.delete {
        display: inline-block;
      }
    }
  }
}
</style>
